// The orders page's stats card: totals over exactly the rows the table below it
// is showing, so it follows the status segments and the search. Numbers come
// from `orderStatsFor`; which items count as drones, batteries, chargers or
// ground kit is the `stat` field on each catalog entry in itemTypes.ts.
import React, { useMemo } from 'react';
import { INSTANCE } from '../instance';
import { StatTile } from '../components/ui/Panel';
import Skeleton from '../components/ui/Skeleton';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { businessToday, formatEventDate } from '../utils/date-utils';
import type { SalesOrder } from '../Customers/types';
import { orderStatsFor, relativeDays } from './orderStats';

interface OrderStatsProps {
  /** The table's filtered rows, not the whole tab. */
  orders: SalesOrder[];
  stages: string[];
  dueField: 'delivery_date' | 'effective_date';
  loading: boolean;
  /** The next-due order the date is pointing at, or null when let go. */
  onHighlight: (orderId: string | null) => void;
  /** Bring that order's row into view. */
  onReveal: (orderId: string) => void;
}

const nf = new Intl.NumberFormat('en-US');

const OrderStats: React.FC<OrderStatsProps> = ({ orders, stages, dueField, loading, onHighlight, onReveal }) => {
  const stats = useMemo(
    () => orderStatsFor(INSTANCE, orders, stages, dueField, businessToday().format('YYYY-MM-DD')),
    [orders, stages, dueField],
  );

  if (loading) {
    return (
      <Skeleton className="h-[4.75rem] rounded-panel" />
    );
  }

  const { count, totals, overdue, nextDue } = stats;

  const overdueChip = overdue > 0 && (
    <Tag tone="bad" className="shrink-0 tabular-nums">
      {overdue} overdue
    </Tag>
  );

  // A button, not a hover target: focus highlights the row as hover does, and
  // a press scrolls it into view. The tooltip wraps the button, since
  // a `title` on a button replaces its accessible name (web/CLAUDE.md).
  const nextDueKey = nextDue && (
    <Tooltip label={`${nextDue.order.so_number}, ${relativeDays(nextDue.daysUntil)}`}>
      <button
        type="button"
        onMouseEnter={() => onHighlight(nextDue.order.id)}
        onMouseLeave={() => onHighlight(null)}
        onFocus={() => onHighlight(nextDue.order.id)}
        onBlur={() => onHighlight(null)}
        onClick={() => onReveal(nextDue.order.id)}
        className="rounded tabular-nums hover:text-on-surface focus-visible:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors duration-150 ease-emphasized"
      >
        Next Due:{' '}
        <span className={nextDue.daysUntil <= 7 ? 'text-primary' : 'text-on-surface'}>
          {formatEventDate(nextDue.due, 'MM/DD')}
        </span>
      </button>
    </Tooltip>
  );

  const tile = (n: number, label: string) => <StatTile label={label} value={n} format={nf.format} />;

  return (
    <section aria-label="Totals for the orders shown">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <div className="relative min-w-0">
          <StatTile
            on="panel"
            className="h-full"
            label={count === 1 ? 'Order' : 'Orders'}
            value={count}
            format={nf.format}
            sub={nextDueKey || undefined}
          />
          {overdueChip && <span className="absolute right-3 top-3">{overdueChip}</span>}
        </div>
        {tile(totals.drones, totals.drones === 1 ? 'Drone' : 'Drones')}
        {tile(totals.batteries, totals.batteries === 1 ? 'Battery' : 'Batteries')}
        {tile(totals.chargers, totals.chargers === 1 ? 'Charger' : 'Chargers')}
        {tile(totals.ground, 'Ground kit')}
      </div>
    </section>
  );
};

export default OrderStats;
